import { DateService } from '@/coreDomainsModule/services/dateService';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { MonthDistance, addMonthDistance } from '@/InventoryControlModule/domains/valueObjects/MonthDistance';
import { IArrival, OrderStatus } from '@/coreDomainsModule/entities/arrival';
import { equalYearMonthInfo } from '@/appLogic/interfaces/date';
import { ArrivalStatusService } from '@/InventoryControlModule/domains/services/arrivalStatusService';
import { ArrivalStatus } from '@/coreDomainsModule/entities/arrival';
import { IParentBusiness } from '@/InventoryControlModule/domains/entities/Business';
import { MonthlyUnPaidArrival } from '@/InventoryControlModule/domains/entities/MonthlyData';

/**
 * 月ごとの計算を行うサービス
 */
export class MonthlyDataCalculationService {
  /**
   * calcMonthで計算した月の入荷を返す
   * @param business - 計算元の取引オブジェクト
   * @param month - 計算対象の月の距離
   */
  public static calcArrival = (business: IBusiness, month: MonthDistance): number => {
    const result = DateService.addMonthsToCurrentDate(month);
    if (business.arrivals === undefined) {
      return 0;
    }

    const arrival = business.arrivals.find((arrival) => {
      return equalYearMonthInfo(arrival, result);
    });
    // 合計を返す
    return arrival?.amount ?? 0;
  };

  /**
   * calcMonthで計算した月の実績を返す
   * @param business - 計算元の取引オブジェクト
   * @param month - 計算対象の月の距離
   */
  public static calcSalesResult = (business: IBusiness, month: MonthDistance): number => {
    const result = DateService.addMonthsToCurrentDate(month);
    if (business.salesResults === undefined) {
      return 0;
    }
    const performance = business.salesResults.find((performance) => {
      return equalYearMonthInfo(performance, result);
    });
    // 合計を返す
    return performance?.amount ?? 0;
  };

  /**
   * calcMonthで計算した月の内示を返す
   * @param business - 計算元の取引オブジェクト
   * @param month - 計算対象の月の距離
   */
  public static calcAnnounce = (business: IBusiness, month: MonthDistance): number => {
    const result = DateService.addMonthsToCurrentDate(month);
    if (business.customerAnnounce === undefined) {
      return 0;
    }
    const indication = business.customerAnnounce.find((indication) => {
      return equalYearMonthInfo(indication, result);
    });
    // 合計を返す
    return indication?.amount ?? 0;
  };

  /**
   * calcMonthで計算した月の仕入先内示を返す
   * @param business - 計算元の取引オブジェクト
   * @param month - 計算対象の月の距離
   */
  public static calcSupplierAnnounce = (business: IBusiness, month: MonthDistance): number | undefined => {
    const result = DateService.addMonthsToCurrentDate(month);
    if ((business as any).supplierAnnounce === undefined) {
      return undefined;
    }
    const indication = (business as any).supplierAnnounce.find((indication: any) => {
      return equalYearMonthInfo(indication, result);
    });
    return indication?.amount;
  };

  /**
   * calcMonthで計算した月の末在庫を返す
   * @param business - 計算元の取引オブジェクト
   * @param month - 計算対象の月の距離
   */
  public static calcEndInventory = (business: IBusiness, month: MonthDistance): number => {
    const result = DateService.addMonthsToCurrentDate(month);
    if (business.mvpInventory === undefined) {
      return 0;
    }
    const endStock = business.mvpInventory.find((endStock) => {
      return equalYearMonthInfo(endStock, result);
    });
    // 合計を返す
    return endStock?.amount ?? 0;
  };

  /**
   * calcMonthで計算した月の在庫回転率（turnOver）を返す
   * @param exceptedEndInventory - 予末在庫
   * @param announce - 内示
   */
  public static calcTurnOver = (exceptedEndInventory: number, announce: number): number => {
    if (exceptedEndInventory === 0 || announce === 0) {
      return 0;
    }
    // 在庫回転率 = 予末在庫 / 内示
    return exceptedEndInventory / announce;
  };

  /**
   * 発注モード向けのcalcMonthで計算した月の予入荷を返す
   * @param business - 計算元の取引オブジェクト
   * @param month - 計算対象の月の距離
   */
  public static calcExpectedArrivalForOrderMode = (business: IBusiness, month: MonthDistance): number => {
    const result = DateService.addMonthsToCurrentDate(month);
    if (business.recommend === undefined) {
      return 0;
    }
    // isFixOrderedがtrueのものだけを抽出
    const expectedArrival = business.recommend.filter((expectedArrival) => {
      return (
        expectedArrival.crossDate !== undefined &&
        equalYearMonthInfo(expectedArrival.crossDate, result) &&
        expectedArrival.status !== undefined &&
        expectedArrival.status !== OrderStatus.Recommended &&
        expectedArrival.status !== OrderStatus.Delivered &&
        expectedArrival.status !== OrderStatus.DisplayEnded &&
        expectedArrival.status !== OrderStatus.Unknown
      );
    });
    // 合計を返す
    return expectedArrival.reduce((prev, current) => {
      return prev + current.amount;
    }, 0);
  };

  /**
   * calcMonthで計算した月の予末在庫を返す
   * @param business - 計算元の取引オブジェクト
   * @param month - 計算対象の月の距離
   */
  public static calcExpectedEndInventory = (business: IBusiness, month: MonthDistance): number => {
    // 予末在庫＝前月末在庫＋当月予入荷-当月内示
    if (month <= 0) {
      return (
        MonthlyDataCalculationService.calcEndInventory(business, addMonthDistance(month, -1)) +
        MonthlyDataCalculationService.calcExpectedArrivalForNormalMode(business, month) -
        MonthlyDataCalculationService.calcAnnounce(business, month)
      );
    }

    // monthDistanceが1以上の場合は、前月の予末在庫を取得する
    return (
      MonthlyDataCalculationService.calcExpectedEndInventory(business, addMonthDistance(month, -1)) +
      MonthlyDataCalculationService.calcExpectedArrivalForNormalMode(business, month) -
      MonthlyDataCalculationService.calcAnnounce(business, month)
    );
  };

  /**
   * calcMonthで計算した月の在庫を返す
   * @param business - 計算元の取引オブジェクト
   */
  public static calcNowInventory = (business: IBusiness): number => {
    return business.inventory ?? 0;
  };

  /**
   * 推奨納期日が最も早い推奨発注情報を返す
   * @param business - 計算元の取引オブジェクト
   */
  public static calcNearestRecommend = (business: IBusiness): IArrival | undefined => {
    if (business.recommend === undefined || business.recommend.length === 0) {
      return undefined;
    }
    const recommendOnlyRecommended = business.recommend.filter(
      (recommend) =>
        (recommend.status === OrderStatus.Recommended ||
          recommend.status === OrderStatus.ApprovalRequested ||
          recommend.status === undefined) &&
        recommend.amount !== -1
    );
    // recommendOnlyRecommendedが空の場合はundefinedを返す
    if (recommendOnlyRecommended.length === 0) return undefined;
    const recommend = recommendOnlyRecommended.reduce((prev, current) => {
      // 片方のcrossDateがundefinedの場合はもう片方を返す
      if (current.crossDate === undefined) return prev;
      if (prev.crossDate === undefined) return current;

      if (
        current.crossDate.year < prev.crossDate.year ||
        (current.crossDate.year === prev.crossDate.year &&
          (current.crossDate.month < prev.crossDate.month ||
            (current.crossDate.month === prev.crossDate.month && current.crossDate.day < prev.crossDate.day)))
      ) {
        return current;
      } else {
        return prev;
      }
    });

    if (recommend.crossDate === undefined) return undefined;
    return recommend;
  };

  /**
   * 発注データを返す
   * @param business - 計算元の取引オブジェクト
   * @param month - 計算対象の月の距離
   */
  public static calcUnPaidArrival = (business: IParentBusiness, month: MonthDistance): MonthlyUnPaidArrival[] => {
    const result = DateService.addMonthsToCurrentDate(month);

    return business.recommend
      .filter((recommend) => {
        return (
          recommend.crossDate !== undefined &&
          equalYearMonthInfo(recommend.crossDate, result) &&
          ArrivalStatusService.OrderStatusToArrivalStatus(recommend.status ?? OrderStatus.Recommended) === ArrivalStatus.Unpaid
        );
      })
      .map((recommend) => {
        const {
          amount,
          arrivalId,
          crossDate,
          day,
          displayId,
          fixOrderDate,
          leadTime,
          month: recommendMonth,
          orderAlert,
          orderNumber,
          status,
          year,
        } = recommend;

        return {
          amount,
          arrivalId,
          crossDate,
          day,
          displayId,
          fixOrderDate,
          leadTime,
          month: recommendMonth,
          orderAlert,
          orderNumber,
          status,
          year,
        };
      });
  };

  /**
   * 承認依頼の発注数量を返す
   * @param business - 計算元の取引オブジェクト
   * @param month - 計算対象の月の距離
   */
  public static calcApprovalRequestedArrival = (business: IParentBusiness, month: MonthDistance): MonthlyUnPaidArrival[] => {
    const result = DateService.addMonthsToCurrentDate(month);

    return business.recommend
      .filter((recommend) => {
        return (
          recommend.crossDate !== undefined &&
          equalYearMonthInfo(recommend.crossDate, result) &&
          ArrivalStatusService.OrderStatusToArrivalStatus(recommend.status ?? OrderStatus.Recommended) === ArrivalStatus.ApprovalRequested
        );
      })
      .map((recommend) => {
        const {
          amount,
          arrivalId,
          crossDate,
          day,
          displayId,
          fixOrderDate,
          leadTime,
          month: recommendMonth,
          orderAlert,
          orderNumber,
          status,
          year,
        } = recommend;

        return {
          amount,
          arrivalId,
          crossDate,
          day,
          displayId,
          fixOrderDate,
          leadTime,
          month: recommendMonth,
          orderAlert,
          orderNumber,
          status,
          year,
        };
      });
  };

  /**
   * ArrivalStatusのリストを返す
   * @param business - 計算元の取引オブジェクト
   * @param month - 計算対象の月の距離
   */
  public static calcArrivalStatusList = (business: IBusiness, month: MonthDistance): ArrivalStatus[] => {
    const result = DateService.addMonthsToCurrentDate(month);

    if (business.recommend === undefined || business.recommend.length === 0 || business.recommend[0]?.amount === -1) {
      return [];
    }

    const arrivalStatusList = business.recommend
      .filter((recommend) => {
        return recommend.crossDate !== undefined && equalYearMonthInfo(recommend.crossDate, result);
      })
      .map((recommend) => {
        // recommendに存在するのにstatusがundefinedの場合は、Recommendedとして扱う
        return ArrivalStatusService.OrderStatusToArrivalStatus(recommend.status ?? OrderStatus.Recommended);
      });

    // ArrivalStatusをソートする
    // Recommended（１） > ApprovalRequested（２） > Unpaid（３） の順に並べる
    const sortedArrivalStatusList = [...new Set(arrivalStatusList)].sort((a, b) => {
      return ArrivalStatusService.ArrivalStatusToPriority(a) - ArrivalStatusService.ArrivalStatusToPriority(b);
    });

    return sortedArrivalStatusList;
  };

  /**
   * 納入済みの発注数量を返す
   * @param business - 計算元の取引オブジェクト
   * @param month - 計算対象の月の距離
   */
  public static calcDeliveredArrival = (business: IBusiness, month: MonthDistance): number => {
    const result = DateService.addMonthsToCurrentDate(month);
    if (business.recommend === undefined) {
      return 0;
    }
    const deliveredArrival = business.recommend.filter((recommend) => {
      return (
        recommend.crossDate !== undefined && equalYearMonthInfo(recommend.crossDate, result) && recommend.status === OrderStatus.Delivered
      );
    });
    // 合計を返す
    return deliveredArrival.reduce((prev, current) => {
      return prev + current.amount;
    }, 0);
  };

  /**
   * 通常モード向けのcalcMonthで計算した月の予入荷を返す
   * 通常モードの予入荷は、当月以降の入荷列の値を指す
   * @param business - 計算元の取引オブジェクト
   * @param month - 計算対象の月の距離
   */
  public static calcExpectedArrivalForNormalMode = (business: IBusiness, month: MonthDistance): number => {
    // 発注モード向けの予入荷 + 納入済みの発注数量
    return (
      MonthlyDataCalculationService.calcExpectedArrivalForOrderMode(business, month) +
      MonthlyDataCalculationService.calcDeliveredArrival(business, month)
    );
  };
}
